import { DELIVERY_STATUS_PRESENTATION, type OutboundWebhookDeliveryViewDto } from '@/types/outboundWebhooks'

interface DeliverySparklineProps {
    /** Newest first, as the API returns them. */
    deliveries: OutboundWebhookDeliveryViewDto[]
    slots?: number
}

/**
 * Recent delivery health at a glance, newest on the right.
 *
 * The point is that a destination with a run of failures is visible in the rail WITHOUT selecting
 * it — otherwise finding the broken one means clicking through every destination in turn.
 *
 * Unfilled slots are short and grey rather than absent, so a destination with three deliveries and
 * one with ten occupy the same width and the rail stays aligned.
 */
export function DeliverySparkline({ deliveries, slots = 10 }: DeliverySparklineProps) {
    const recent = deliveries.slice(0, slots).reverse()
    const padding = Math.max(0, slots - recent.length)

    const summary = recent.length === 0
        ? 'No deliveries yet'
        : `Last ${recent.length} deliveries: ${recent.filter(d => d.Status === 'Delivered').length} delivered, ${recent.filter(d => d.Status === 'Failed').length} failed`

    return (
        <span className="flex items-end gap-[2.5px] h-4" role="img" aria-label={summary}>
            {Array.from({ length: padding }).map((_, i) => (
                <span key={`pad-${i}`} className="w-[5px] h-[5px] rounded-[1.5px] bg-slate-200 dark:bg-slate-600" />
            ))}
            {recent.map(delivery => (
                <span
                    key={delivery.Id}
                    className={`w-[5px] h-[15px] rounded-[1.5px] ${
                        (DELIVERY_STATUS_PRESENTATION[delivery.Status] ?? DELIVERY_STATUS_PRESENTATION.Cancelled).dotClassName
                    }`}
                />
            ))}
        </span>
    )
}
